
import React, { useState, useEffect } from 'react';
import { ArrowRight, Star, Heart, Share2, MapPin, Clock, CheckCircle, Search, ArrowLeft, Loader2, ShoppingBag } from 'lucide-react';
import { Store, Product } from '../types';
import { ProductCardLoader } from './ProductCardLoader';
import { OptimizedImage } from './OptimizedImage';
import { useShopProducts } from '../hooks/useFirestoreData';
import { doc, onSnapshot, db } from '../firebase';

interface StoreDetailsPageProps {
    store: Store;
    products: Product[]; // Kept for interface compatibility but ignored
    isDark: boolean;
    onClose: () => void;
    onOpenDetails: (product: Product) => void;
    onAddToCart: (e: React.MouseEvent, product: Product) => void;
    addedToCartMap: { [key: number]: boolean };
}

export const StoreDetailsPage: React.FC<StoreDetailsPageProps> = ({
    store,
    isDark,
    onClose,
    onOpenDetails,
    onAddToCart,
    addedToCartMap
}) => {
    const [isFavorite, setIsFavorite] = useState(false);
    const [showCopied, setShowCopied] = useState(false);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const { products: storeProducts, loading } = useShopProducts(store.id);
    const [storeSettings, setStoreSettings] = useState<{ title?: string; rating?: number; description?: string } | null>(null);

    // Fetch storeSettings
    useEffect(() => {
        if (!store.id) return;
        const storeSettingsRef = doc(db, 'storeSettings', store.id.toString());
        const unsubscribe = onSnapshot(storeSettingsRef, (snap) => {
            if (snap.exists()) {
                setStoreSettings(snap.data() as any);
            } else {
                setStoreSettings(null);
            }
        }, (err) => {
            console.error("Error fetching store settings:", err);
            setStoreSettings(null);
        });
        return () => unsubscribe();
    }, [store.id]);

    // Load favorites from localStorage
    useEffect(() => {
        try {
            const favs: string[] = JSON.parse(localStorage.getItem('rashid_fav_stores') || '[]');
            setIsFavorite(favs.includes(String(store.id)));
        } catch { setIsFavorite(false); }
    }, [store.id]);

    const toggleFavorite = () => {
        try {
            let favs: string[] = JSON.parse(localStorage.getItem('rashid_fav_stores') || '[]');
            const sid = String(store.id);
            if (favs.includes(sid)) {
                favs = favs.filter(f => f !== sid);
            } else {
                favs.push(sid);
            }
            localStorage.setItem('rashid_fav_stores', JSON.stringify(favs));
            setIsFavorite(!isFavorite);
        } catch (e) { console.error(e); }
    };

    const handleShare = async () => {
        const url = `${window.location.origin}/?store=${store.id}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: store.name, url });
            } else {
                await navigator.clipboard.writeText(url);
                setShowCopied(true);
                setTimeout(() => setShowCopied(false), 2000);
            }
        } catch {
            await navigator.clipboard.writeText(url);
            setShowCopied(true);
            setTimeout(() => setShowCopied(false), 2000);
        }
    };

    // Use storeSettings if available, otherwise fallback to store data
    const displayTitle = storeSettings?.title || store.name;
    const displayDescription = storeSettings?.description || store.description;

    // Calculate dynamic store ratings based on its products
    const storeAvgRating = storeProducts.length > 0
        ? storeProducts.reduce((sum, p) => sum + (p.rating || 5), 0) / storeProducts.length
        : (storeSettings?.rating ?? store.rating);

    const storeTotalReviews = storeProducts.reduce((sum, p) => sum + (p.reviewCount || 0), 0);

    return (
        // Lowered z-index to 45 (below Header's 50)
        <div className={`fixed inset-0 z-[45] overflow-y-auto custom-scrollbar overscroll-y-contain animate-fade-in ${isDark ? 'bg-[#0f1115]' : 'bg-[#f5f6f7]'}`} style={{ WebkitOverflowScrolling: 'touch' }}>

            {/* Main Content Container - Fixed padding-top to prevent content hidden behind header */}
            <div className="w-full max-w-[1400px] mx-auto pt-28 md:pt-32">

                {/* Cover Section with Floating Buttons */}
                <div className="relative w-[95%] mx-auto h-[250px] md:h-[350px] rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10 group">
                    <OptimizedImage
                        src={store.banner || store.coverImage || store.logo || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800'}
                        alt="Banner"
                        aspectRatio="2.4/1"
                        priority={true}
                        className="w-full h-full transition-transform duration-700 group-hover:scale-105"
                    />

                    {/* Gradient Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0f1115] via-transparent to-black/40' : 'from-[#f5f6f7] via-transparent to-black/20'}`}></div>

                    {/* Floating Header Actions - Positioned on Cover */}
                    <div className="absolute top-0 left-0 w-full p-4 md:p-6 flex justify-between items-start z-30" style={{ direction: 'ltr' }}>
                        {/* Back Button */}
                        <button
                            onClick={onClose}
                            className={`flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full font-black text-xs md:text-sm backdrop-blur-md shadow-lg border transition-all active:scale-95 hover:scale-105 ${isDark ? 'bg-black/40 text-white border-white/20 hover:bg-black/60' : 'bg-white/80 text-slate-800 border-white/40 hover:bg-white'}`}
                        >
                            <ArrowLeft size={16} className="md:w-[18px]" />
                            <span>عودة</span>
                        </button>

                        {/* Actions */}
                        <div className="flex gap-2 md:gap-3">
                            <button
                                onClick={() => toggleFavorite()}
                                className={`w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center backdrop-blur-md shadow-lg border transition-transform active:scale-90 hover:scale-105 ${isDark ? 'bg-black/40 text-white border-white/20 hover:bg-black/60' : 'bg-white/80 text-slate-800 border-white/40 hover:bg-white'} ${isFavorite ? 'text-red-500 !bg-red-500/10 !border-red-500/30' : ''}`}
                            >
                                <Heart size={18} className="md:w-[20px]" fill={isFavorite ? "currentColor" : "none"} />
                            </button>
                            <div className="relative">
                                <button
                                    onClick={handleShare}
                                    className={`w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center backdrop-blur-md shadow-lg border transition-transform active:scale-90 hover:scale-105 ${isDark ? 'bg-black/40 text-white border-white/20 hover:bg-black/60' : 'bg-white/80 text-slate-800 border-white/40 hover:bg-white'}`}
                                >
                                    <Share2 size={18} className="md:w-[20px]" />
                                </button>
                                {showCopied && (
                                    <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-green-500 text-white text-[10px] font-black rounded-lg shadow-lg whitespace-nowrap animate-fade-in-up">
                                        ✓ تم نسخ الرابط
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Store Information Card (Overlapping) */}
                <div className="relative px-4 md:px-8 -mt-20 z-20 mb-8" style={{ direction: 'rtl' }}>
                    <div className={`rounded-[1.5rem] md:rounded-[2.5rem] p-5 md:p-8 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.15)] border backdrop-blur-xl ${isDark ? 'bg-[#1a1c1e]/95 border-white/10 text-white shadow-black/50' : 'bg-white/95 border-white/50 text-slate-800 shadow-slate-200/50'}`}>

                        {/* Header Row */}
                        <div className="flex items-start gap-4 mb-6">
                            <div className={`w-20 h-20 md:w-28 md:h-28 rounded-2xl overflow-hidden border-4 shadow-lg shrink-0 ${isDark ? 'border-[#1a1c1e] bg-white' : 'border-white bg-white'}`}>
                                <OptimizedImage src={store.logo} alt="Logo" aspectRatio="1/1" objectFit="contain" className="w-full h-full p-2" />
                            </div>

                            <div className="flex-1 pt-1 md:pt-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <h1 className="text-2xl md:text-4xl font-black">{displayTitle}</h1>
                                    <div className={`px-2 py-0.5 rounded-md text-[10px] md:text-xs font-black border animate-pulse ${isDark ? 'bg-blue-500/10 border-blue-500/30 text-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.3)]' : 'bg-blue-50 border-blue-200 text-blue-600'}`}>
                                        تحقق رشيد
                                    </div>
                                </div>
                                <div className="flex items-center flex-wrap gap-2 md:gap-3">
                                    <div className={`px-3 py-1.5 md:px-4 md:py-1.5 rounded-full text-[10px] md:text-xs font-black border flex items-center gap-1.5 md:gap-2 whitespace-nowrap ${isDark ? 'bg-white/5 border-white/10 text-white shadow-xl shadow-black/25' : 'bg-slate-100 border-slate-200 text-slate-700 shadow-sm'}`}>
                                        <ShoppingBag size={14} className="text-blue-500 shrink-0" />
                                        <span>{loading ? '...' : storeProducts.length} منتج متاح</span>
                                    </div>
                                    <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full border whitespace-nowrap ${isDark ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-100 text-emerald-600'}`}>
                                        <CheckCircle size={14} className="shrink-0" />
                                        <span className="text-[10px] md:text-xs font-black">متجر موثوق</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col items-end gap-2">
                                <div className="flex items-center gap-1 bg-yellow-400/10 px-3 py-1.5 rounded-xl border border-yellow-400/20">
                                    <span className="font-black text-yellow-500 text-sm md:text-xl">{storeAvgRating.toFixed(1)}</span>
                                    <Star size={16} className="text-yellow-500 fill-yellow-500" />
                                </div>
                                <span className={`text-[10px] font-bold underline transition-colors ${storeTotalReviews > 0 ? 'text-blue-500' : (isDark ? 'text-slate-500' : 'text-slate-400')}`}>
                                    {storeTotalReviews > 0 ? `(${storeTotalReviews} تقييم حقيقي)` : '(توصيات رشيد)'}
                                </span>
                            </div>
                        </div>

                        {/* Badges Row - Dynamic Features */}
                        <div className="flex flex-wrap gap-2 md:gap-3 mb-6">
                            {(store.features && store.features.length > 0) ? (
                                store.features.map(feature => {
                                    let IconComponent = CheckCircle;
                                    if (feature.icon === 'truck') IconComponent = Clock; // Reusing Lucide icons as fallback if specific ones not imported
                                    if (feature.icon === 'shield') IconComponent = CheckCircle;
                                    if (feature.icon === 'star') IconComponent = Star;
                                    if (feature.icon === 'clock') IconComponent = Clock;

                                    return (
                                        <div key={feature.id} className={`flex items-center gap-2 px-3 py-2 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-black border transition-all hover:scale-105 shadow-sm ${isDark ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' : 'bg-blue-50 border-blue-100 text-blue-600'}`}>
                                            <IconComponent size={14} />
                                            <span>{feature.text}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <>
                                    <div className={`flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs font-bold border ${isDark ? 'bg-white/5 border-white/10 text-slate-300' : 'bg-slate-100 border-slate-200 text-slate-600'}`}>
                                        <CheckCircle size={14} className="text-blue-500" />
                                        <span>{store.category}</span>
                                    </div>
                                    <div className={`flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs font-bold border ${isDark ? 'bg-white/5 border-white/10 text-slate-300' : 'bg-slate-100 border-slate-200 text-slate-600'}`}>
                                        <Clock size={14} className="text-green-500" />
                                        <span>{store.deliveryTime}</span>
                                    </div>
                                    <div className={`flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs font-bold border ${isDark ? 'bg-white/5 border-white/10 text-slate-300' : 'bg-slate-100 border-slate-200 text-slate-600'}`}>
                                        <MapPin size={14} className="text-red-500" />
                                        <span>القاهرة، مصر</span>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Description */}
                        <div className="relative">
                            <p className={`text-sm md:text-lg leading-relaxed transition-all duration-300 ${!isDescriptionExpanded ? 'line-clamp-2' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {displayDescription}
                            </p>
                            {displayDescription && displayDescription.length > 100 && (
                                <button
                                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                    className={`text-xs md:text-sm font-black mt-1 hover:underline transition-colors ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
                                >
                                    {isDescriptionExpanded ? 'عرض أقل' : 'عرض المزيد...'}
                                </button>
                            )}
                        </div>

                    </div>
                </div>

                {/* 4️⃣ Products Section (Replaced with Grid) */}
                <div className="px-4 md:px-8 mb-4" style={{ direction: 'rtl' }}>

                    {/* Section Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="relative">
                            <h2 className={`text-xl md:text-3xl font-black relative z-10 px-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>منتجات المتجر</h2>
                            <div className={`absolute bottom-1 right-0 w-full h-3 -z-0 rounded-full opacity-50 ${isDark ? 'bg-blue-900/40' : 'bg-blue-100'}`}></div>
                        </div>

                        <div className={`p-2.5 rounded-full border transition-colors ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                            <Search size={20} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
                        </div>
                    </div>

                    {/* 5️⃣ Products Sections */}
                    {loading ? (
                        <div className="w-full py-20 flex justify-center">
                            <Loader2 className="animate-spin w-10 h-10 text-blue-500" />
                        </div>
                    ) : storeProducts.length > 0 ? (
                        <div className="space-y-12">
                            {/* Sub-categories handling */}
                            {(() => {
                                const grouped: { [key: string]: Product[] } = {};
                                const subCats = store.subCategories || [];

                                // Initialize groups
                                subCats.forEach(cat => grouped[cat] = []);
                                const otherLabel = "أقسام أخرى";
                                grouped[otherLabel] = [];

                                // Group products
                                storeProducts.forEach(product => {
                                    const sub = product.shopSubCategory;
                                    if (sub && grouped[sub]) {
                                        grouped[sub].push(product);
                                    } else {
                                        grouped[otherLabel].push(product);
                                    }
                                });

                                // Render sections that have products
                                return [...subCats, otherLabel].map(cat => {
                                    const productsInCat = grouped[cat];
                                    if (productsInCat.length === 0) return null;

                                    return (
                                        <div key={cat} className="space-y-6">
                                            <div className="flex items-center gap-4">
                                                <h3 className={`text-lg md:text-2xl font-black ${isDark ? 'text-white/90' : 'text-slate-800'}`}>{cat}</h3>
                                                <div className={`flex-1 h-px ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}></div>
                                                <span className={`text-xs font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{productsInCat.length} منتج</span>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6" style={{ direction: 'rtl' }}>
                                                {productsInCat.map((product, idx) => (
                                                    <ProductCardLoader
                                                        key={product.id}
                                                        product={product}
                                                        isDark={isDark}
                                                        onOpenDetails={onOpenDetails}
                                                        onAddToCart={onAddToCart}
                                                        isAdded={!!addedToCartMap[product.id as number]}
                                                        priority={idx < 4}
                                                        isRow={false}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    ) : (
                        <div className="w-full py-20 flex flex-col items-center justify-center text-center opacity-60">
                            <Search size={48} className="mb-4 text-slate-400" />
                            <p className={`font-bold text-lg ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>لا توجد منتجات متاحة حالياً في هذا المتجر</p>
                        </div>
                    )}
                </div>

                {/* Footer in Store Page */}
                <div className={`w-full mt-2 border-t pt-4 pb-4 flex items-center justify-between px-4 ${isDark ? 'border-white/10' : 'border-slate-200'}`} style={{ direction: 'rtl' }}>
                    <div className="text-right flex flex-col gap-2">
                        <p className={`text-[10px] md:text-sm font-bold ${isDark ? 'text-white/40' : 'text-slate-400'}`}>© 2024 Rashid Online. جميع الحقوق محفوظة.</p>
                        <div className={`flex items-center gap-3 text-[10px] md:text-xs font-bold ${isDark ? 'text-white/30' : 'text-slate-400/80'}`}>
                            <span className="cursor-pointer hover:text-blue-500 transition-colors">نبذة عننا</span>
                            <span>•</span>
                            <span className="cursor-pointer hover:text-blue-500 transition-colors">السياسات</span>
                            <span>•</span>
                            <span className="cursor-pointer hover:text-blue-500 transition-colors">شروط الاستخدام</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <span className={`font-black text-xs md:text-sm ml-2 hidden md:inline ${isDark ? 'text-white/60' : 'text-slate-500'}`}>للتواصل :</span>
                        <a href="https://www.facebook.com/share/1HSzdBexRb/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-10 md:h-10 bg-[#1877F2] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform border border-white/20">
                            <svg className="w-4 h-4 md:w-5 md:h-5 text-white fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                        </a>
                        <a href="https://wa.me/yournumber" target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-10 md:h-10 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform border border-white/20">
                            <svg className="w-4 h-4 md:w-5 md:h-5 text-white fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
                        </a>
                        <a href="mailto:rashidonlineteam@gmail.com" className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform border border-white/20 overflow-hidden p-0">
                            <img src="https://raw.githubusercontent.com/yousefkatato11-creator/main/main/1a615a9b-426b-4100-9f4c-d4820999e657_20260204_040518_%D9%A0%D9%A0%D9%A0%D9%A0.png" alt="Email" className="w-full h-full object-cover p-1" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
